// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                                       from 'react';
import {
    getDownloadURL,
    getStorage,
    ref,
}                                       from 'firebase/storage';
import { ReactSVG }                     from 'react-svg';

// ===== Components =====

import { Transition }                   from '../Transition';
import Spinner                          from '../Spinner';
import NotificationGroup                from '../NotificationGroup';

// ===== Services =====

import {
    playAudio,
    recordUserAction,
    detectTouchDevice,
    getStorageErrorMessage,
    getUser,
    getNotifications,
    markNotificationAsRead,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
}                                       from '../../services';
import useLazyAudioRef                                   from '../../services/useLazyAudioRef';

// ===== Hooks =====

import {
    useLatest,
    useMediaQuality,
}                                       from '../../hooks';

// ===== Interfaces =====

import {
    IPartialUserItem,
    IDimension,
    INotificationItem,
    INotificationGroupItem,
}                                       from '../../interfaces';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    USER_ACTION_TYPE,
    STORAGE_ERROR_CODE,
}                                       from '../../enums';

// ===== Sounds =====

import DialogExpand                     from '../../sounds/swoosh_in.mp3';
// eslint-disable-next-line import/no-duplicates
import DialogContract                   from '../../sounds/swoosh_out.mp3';
// eslint-disable-next-line import/no-duplicates
import DismissNotification              from '../../sounds/swoosh_out.mp3';

// ===== Images =====

import BellIcon                         from '../../images/bell.svg';
import PixelBellIcon                    from '../../images/8-bit_bell.svg';
import FaviconIcon                      from '../../images/favicon.png';
import FaviconNotifIcon                 from '../../images/faviconNotif.png';

// ===== Constants =====

import {
    DEFAULT_AUDIO_VOLUME,
    HOVER_TARGET_CLASSNAME,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    DIALOG_BODY_CONTAINER_TRANSITION_DURATION,
    DIALOG_BODY_OPACITY_DELAY_DURATION,
    NOTIFICATION_DIALOG__BORDER_RADIUS_MULTIPLIER,
    HEADER_BUTTON_TRANSITION_DURATION,
    BODY_FONT_SIZE,
    BELL_BUTTON_HEIGHT_MULTIPLIER,
    NOTIFICATION_DIALOG_CONTRACTED_TOP_MULTIPLIER,
    NOTIFICATION_DIALOG_EXPANDED_TOP_MULTIPLIER,
    NOTIFICATION_DIALOG_BODY_WIDTH_MULTIPLIER,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    DialogBody,
    DialogBodyContainer,
    BellIconContainer,
    NoNotificationsContainer,
    NoNotificationsText,
    NoNotificationsIcon,
    NotificationBadge,
    SpinnerContainer,
}                                       from './styles';
import { theme }                        from '../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    user: IPartialUserItem | null,
    style: any,
    hasSound: boolean,
    lightBackground: boolean,
    showLightBackground: boolean,
    currentSessionId: string | null,
    viewportDimensions: IDimension,
    isExpanded: boolean,
    isRecognizedUser: boolean,
    userProfileDialogExpanded: boolean,
    refreshNotifications: boolean,
    setIsExpanded: (isExpanded: boolean) => void,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setRefreshNotifications: React.Dispatch<React.SetStateAction<boolean>>,
}
function NotificationDialog({
    customCursorActive,
    user,
    style,
    hasSound,
    lightBackground,
    showLightBackground,
    currentSessionId,
    isExpanded,
    isRecognizedUser,
    userProfileDialogExpanded,
    refreshNotifications,
    setIsExpanded,
    onCursorEnter,
    onCursorLeave,
    setRefreshNotifications,
}: Props): JSX.Element {
    // ===== General Constants =====

    const PLACEHOLDER_HEIGHT = 2.25 * BODY_FONT_SIZE;

    // ===== Refs =====

    // ----- Sound Clips
    const dialogExpandClip = useLazyAudioRef({
        src: DialogExpand,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const dialogContractClip = useLazyAudioRef({
        src: DialogContract,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const dismissNotifClip = useLazyAudioRef({
        src: DismissNotification,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const isMountedRef = useRef<boolean>(true);

    // ===== State =====

    const [hasFetchedNotifications, setHasFetchedNotifications] = useState<boolean>(false);
    const [notifications, setNotifications] = useState<INotificationItem[]>([]);
    const [notificationGroups, setNotificationGroups] = useState<INotificationGroupItem[]>([]);
    const [notificationAuthors, setNotificationAuthors] = useState<Map<string, IPartialUserItem>>(new Map());
    const [notificationAvatars, setNotificationAvatars] = useState<Map<string, string>>(new Map());

    // ===== Animation Constants =====

    const DIALOG_BODY_OPACITY_DURATION = 150;
    const NOTIFICATION_BADGE_TRANSITION_DURATION = 150;
    const NOTIFICATION_COUNT_TRANSITION_DURATION = 500;

    // ===== Methods =====

    const fetchNotificationsLatest = useLatest({
        notifications,
        notificationGroups,
        hasFetchedNotifications,
    });
    const fetchNotifications = (): void => {
        if (user && refreshNotifications) {
            getNotifications({
                test: isTestMode(),
            }).then((payload) => {
                if (!isMountedRef.current) return;
                const cur = fetchNotificationsLatest.current;
                if (
                    payload.notifications.length !== cur.notifications.length
                    || payload.groups.size !== cur.notificationGroups.length
                ) {
                    const fetchedNotifications = [...payload.notifications];
                    const notificationGroupMap = payload.groups;
                    fetchedNotifications.sort((a, b) => b.timestamp - a.timestamp);
                    setNotifications(fetchedNotifications);
                    const groups = Array.from(notificationGroupMap.values());
                    groups.sort((a, b) => b.mostRecentTimestamp - a.mostRecentTimestamp);
                    setNotificationGroups(groups);
                    setHasFetchedNotifications(true);
                    setFavicon(fetchedNotifications.length);
                } else if (
                    payload.groups.size === 0
                    && !cur.hasFetchedNotifications
                ) {
                    setHasFetchedNotifications(true);
                    setFavicon(0);
                }

                setRefreshNotifications(false);
            });
        }
    };

    const fetchNotificationAuthors = (): void => {
        const authorPromises: Promise<IPartialUserItem | null>[] = [];
        for (let i = 0; i < notifications.length; i += 1) {
            const { userId } = notifications[i];
            authorPromises.push(getUser({
                test: isTestMode(),
                userId,
            }));
        }
        Promise.all(authorPromises).then((authors) => {
            if (!isMountedRef.current) return;
            const notifAuthors: Map<string, IPartialUserItem> = new Map();
            authors.forEach((item, index) => {
                if (item != null) notifAuthors.set(notifications[index].userId, item);
            });
            setNotificationAuthors(notifAuthors);
        });
    };

    const setFavicon = (numNotifications: number): void => {
        let link: HTMLLinkElement | null = document.querySelector('link[rel*=\'icon\']');

        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        if (numNotifications > 0) {
            link.href = FaviconNotifIcon;
        } else {
            link.href = FaviconIcon;
        }
    };

    const onBellEnter = (e: React.MouseEvent | React.TouchEvent | React.SyntheticEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.notificationButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onBellLeave = (e: React.MouseEvent | React.TouchEvent | React.SyntheticEvent): void => {
        onCursorLeave(e);
    };

    const onToggleExpand = (): void => {
        const wasExpanded = isExpanded;
        setIsExpanded(!isExpanded);

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: wasExpanded
                    ? USER_ACTION_TYPE.hideNotifications
                    : USER_ACTION_TYPE.viewNotifications,
                sessionId: currentSessionId,
            });
        }

        // Play Sound
        if (
            !isExpanded
            && hasSound
            && dialogExpandClip.current
        ) {
            dialogExpandClip.current.pause();
            dialogExpandClip.current.currentTime = 0;
            playAudio(dialogExpandClip.current);
        } else if (
            isExpanded
            && hasSound
            && dialogContractClip.current
        ) {
            dialogContractClip.current.pause();
            dialogContractClip.current.currentTime = 0;
            playAudio(dialogContractClip.current);
        }
    };

    const dismissNotification = (notif: INotificationItem): Promise<void> => {
        // Play Sound
        if (hasSound && dismissNotifClip.current) {
            dismissNotifClip.current.pause();
            dismissNotifClip.current.currentTime = 0;
            playAudio(dismissNotifClip.current);
        }

        // Make sure cursor doesn't have odd behavior when button immediately disappears
        onCursorLeave();

        return markNotificationAsRead({
            test: isTestMode(),
            notificationId: notif.id,
        }).then(() => {
            setRefreshNotifications(true);

            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.readNotification,
                    sessionId: currentSessionId,
                });
            }
        });
    };

    // ===== Side Effects =====

    const { quality } = useMediaQuality({ preferHighQuality: false });

    /**
     * Loads all page sound files into audio elements
     */
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (user && refreshNotifications) {
            fetchNotifications();
        }
    }, [user, refreshNotifications]);

    /**
     * Fetches user items of all notifications
     */
    useEffect(() => {
        if (
            notifications.length > 0
            && notificationAuthors.size === 0
        ) {
            fetchNotificationAuthors();
        }
    }, [notifications]);

    /**
     * Fetches avatars of all notification authors
     */
    useEffect(() => {
        if (
            notificationAuthors.size > 0
            && notificationAvatars.size === 0
        ) {
            let active = true;
            const storage = getStorage();
            const avatars: Map<string, string> = new Map();
            const avatarPromises = Array.from(notificationAuthors.values()).map(async (author) => {
                if (!author || !author.avatarFilePath) return;
                const pathParts = author.avatarFilePath.split('.');
                const dynamicImagePath = `${pathParts[0]}_${quality}.${pathParts[1]}`;
                try {
                    const imageURL = await getDownloadURL(ref(storage, dynamicImagePath));
                    if (!active || !isMountedRef.current) return;
                    avatars.set(author.id, imageURL);
                } catch (error: any) {
                    // We assume cloud function has not yet generated medium image yet
                    if (error.code === STORAGE_ERROR_CODE.objectNotFound) {
                        try {
                            const imageURL = await getDownloadURL(ref(storage, author.avatarFilePath));
                            if (!active || !isMountedRef.current) return;
                            avatars.set(author.id, imageURL);
                        } catch (err: any) {
                            if (currentSessionId) {
                                recordUserAction({
                                    test: isTestMode(),
                                    type: USER_ACTION_TYPE.error,
                                    sessionId: currentSessionId,
                                    payload: {
                                        // eslint-disable-next-line max-len
                                        error: getStorageErrorMessage(err.code as STORAGE_ERROR_CODE),
                                    },
                                });
                            }
                            throw Error(getStorageErrorMessage(err.code as STORAGE_ERROR_CODE));
                        }
                    } else {
                        if (currentSessionId) {
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                                },
                            });
                        }
                        throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
                    }
                }
            });

            Promise.all(avatarPromises).then(() => {
                if (!active || !isMountedRef.current) return;
                setNotificationAvatars(new Map(avatars));
            });

            return () => {
                active = false;
            };
        }
        return undefined;
    }, [notificationAuthors]);

    // ===== Rendering =====

    return (
        <Container
            isExpanded={isExpanded}
            isRecognizedUser={isRecognizedUser}
            lightBackground={lightBackground}
            showLightBackground={showLightBackground}
            userProfileDialogExpanded={userProfileDialogExpanded}
            transitionDuration={HEADER_BUTTON_TRANSITION_DURATION}
            style={style}
            data-cursor-surface={lightBackground ? 'light' : undefined}
        >
            <BellIconContainer
                height={BELL_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                detectTouchDevice={detectTouchDevice(document)}
                lightBackground={lightBackground}
                showLightBackground={showLightBackground}
                customCursorActive={customCursorActive}
                className={HOVER_TARGET_CLASSNAME}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e: React.MouseEvent) => onBellEnter(e),
                    onMouseLeave: (e: React.MouseEvent) => onBellLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onToggleExpand })}
            >
                <ReactSVG
                    src={PixelBellIcon}
                />
            </BellIconContainer>
            <Transition
                in={notificationGroups.length > 0}
                timeout={{
                    enter: NOTIFICATION_BADGE_TRANSITION_DURATION,
                    exit: NOTIFICATION_BADGE_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <NotificationBadge
                        count={notificationGroups.length}
                        transitionDuration={NOTIFICATION_COUNT_TRANSITION_DURATION}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'left',
                                offset: 10,
                                duration: NOTIFICATION_BADGE_TRANSITION_DURATION,
                                easing: theme.motion.eagerEasing,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'left',
                                offset: 10,
                            })[state],
                        }}
                    />
                )}
            </Transition>
            <DialogBodyContainer
                contractedTop={NOTIFICATION_DIALOG_CONTRACTED_TOP_MULTIPLIER * BODY_FONT_SIZE}
                expandedTop={NOTIFICATION_DIALOG_EXPANDED_TOP_MULTIPLIER * BODY_FONT_SIZE}
                borderRadius={NOTIFICATION_DIALOG__BORDER_RADIUS_MULTIPLIER * BODY_FONT_SIZE}
                width={NOTIFICATION_DIALOG_BODY_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                isExpanded={isExpanded}
                isRecognizedUser={isRecognizedUser}
                transitionDuration={DIALOG_BODY_CONTAINER_TRANSITION_DURATION}
            >
                <DialogBody
                    isExpanded={isExpanded}
                    opacityDuration={DIALOG_BODY_OPACITY_DURATION}
                    transitionDelayDuration={DIALOG_BODY_OPACITY_DELAY_DURATION}
                >
                    {notifications.length > 0
                        ? (
                            notificationGroups.map((group) => (
                                <NotificationGroup
                                    customCursorActive={customCursorActive}
                                    group={group}
                                    user={user}
                                    currentSessionId={currentSessionId}
                                    dismissNotification={dismissNotification}
                                    notificationAuthors={notificationAuthors}
                                    notificationAvatars={notificationAvatars}
                                    setRefreshNotifications={setRefreshNotifications}
                                    onCursorEnter={onCursorEnter}
                                    onCursorLeave={onCursorLeave}
                                />
                            ))
                        ) : (
                            <NoNotificationsContainer
                                height={!hasFetchedNotifications ? PLACEHOLDER_HEIGHT : undefined}
                            >
                                {hasFetchedNotifications
                                    ? (
                                        <>
                                            <NoNotificationsIcon>
                                                <ReactSVG
                                                    src={BellIcon}
                                                />
                                            </NoNotificationsIcon>
                                            <NoNotificationsText>
                                                No notifications
                                            </NoNotificationsText>
                                        </>
                                    ) : (
                                        <SpinnerContainer>
                                            <Spinner
                                                lightBackground
                                            />
                                        </SpinnerContainer>
                                    )}
                            </NoNotificationsContainer>
                        )}
                </DialogBody>
            </DialogBodyContainer>
        </Container>
    );
}

export default NotificationDialog;
